'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/src/i18n/navigation';
import { area as areaApi, type AreaResponse, type AutocompleteResult } from '@/src/lib/api/endpoints/area';
import { useCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import { useRecentSearch, useUpdateRecentSearch } from '@/src/lib/hooks/query/useRecentSearch';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import { usePrepareMapForAreaNavigation, usePrepareMapForListingNavigation } from '@/src/lib/hooks/map';
import { getMoreFiltersCount } from '@/src/lib/filters/filterLabels';
import { FilterContext } from '@/src/lib/filters/FilterProvider';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { resolveListingAutocompleteHref } from '@/src/lib/utils/resolveListingAutocompleteHref';
import { buildRecentSearchBody, getAreaBounds } from '@/src/lib/savedSearches';
import { buildSearchUrl } from './buildSearchUrl';

export type ListingSearchMode = 'live' | 'navigate';

type UseListingSearchStateArgs = {
  mode: ListingSearchMode;
  nearMeArea?: AreaResponse | null;
  seedFromRecentSearch?: boolean;
};

type UseListingSearchStateReturn = {
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  area: AreaResponse | AutocompleteResult | null;
  setArea: (result: AutocompleteResult) => void;
  submit: () => void;
  searchArea: (result: AutocompleteResult) => void;
  isPending: boolean;
  activeFilterCount: number;
  markUserInteraction: () => void;
};

/**
 * Encapsulates the live-vs-navigate state divergence so child components
 * don't have to know which mode they're in.
 *
 * - `live` (sm / SRP header): every filter mutation immediately calls
 *   `useFilters().setFilters(...)`, which writes URL via FilterProviderAppRouter.
 *   `submit()` is a no-op; the search icon is decorative confirmation.
 *
 * - `navigate` (lg / homepage hero): filters mutate a local draft. `submit()`
 *   builds the SRP URL via `buildSearchUrl` and pushes it inside a transition.
 */
export function useListingSearchState({
  mode,
  nearMeArea,
  seedFromRecentSearch,
}: UseListingSearchStateArgs): UseListingSearchStateReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Read FilterContext optionally — `navigate` mode (homepage hero) doesn't
  // mount a FilterProvider, while `live` mode (SRP/HDP) always does. Live
  // mode enforces the provider below.
  const filterContext = useContext(FilterContext);
  if (mode === 'live' && !filterContext) {
    throw new Error('ListingSearch mode="live" requires a FilterProvider ancestor');
  }
  const { data: currentArea } = useCurrentArea();
  const { data: currentUser } = useCurrentUser();
  const { mutate: updateRecentSearch } = useUpdateRecentSearch();
  const prepareMapForAreaNavigation = usePrepareMapForAreaNavigation();
  const prepareMapForListingNavigation = usePrepareMapForListingNavigation();

  const [draftFilters, setDraftFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [draftArea, setDraftArea] = useState<AutocompleteResult | null>(null);
  const [isPending, startTransition] = useTransition();
  // Discards a stale listing-detail resolution if the user picks another
  // result before the in-flight fetch for the previous one settles.
  const latestListingRequestRef = useRef<string | null>(null);

  const isLoggedIn = currentUser?.loggedIn ?? false;
  const shouldSeedRecentSearch = (seedFromRecentSearch ?? false) && mode === 'navigate' && isLoggedIn;
  const { data: recentSearch } = useRecentSearch({ enabled: shouldSeedRecentSearch });

  // Seed the pristine draft with the recent search; any form interaction cancels seeding for good.
  const userTouchedRef = useRef(false);
  const markUserInteraction = useCallback(() => {
    userTouchedRef.current = true;
  }, []);
  const setDraftFiltersFromUser = useCallback((next: FilterState) => {
    userTouchedRef.current = true;
    setDraftFilters(next);
  }, []);

  useEffect(() => {
    if (!shouldSeedRecentSearch || !recentSearch || userTouchedRef.current) return;
    // The draft is untouched here, so absent fields reset it — a cleared or
    // locationless recent search must not leave older seeded criteria behind.
    setDraftFilters(recentSearch.filters ?? DEFAULT_FILTERS);
    setDraftArea(recentSearch.area ?? null);
  }, [shouldSeedRecentSearch, recentSearch]);

  // Logout must clear seeded values (they're the previous user's data) and re-arm seeding.
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !isLoggedIn) {
      setDraftFilters(DEFAULT_FILTERS);
      setDraftArea(null);
      userTouchedRef.current = false;
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const filters = mode === 'live' ? filterContext!.filters : draftFilters;
  const setFilters = mode === 'live' ? filterContext!.setFilters : setDraftFiltersFromUser;

  const buildLiveAreaUrl = useCallback(
    (resourceId: string) => {
      const baseUrl = buildSearchUrl(resourceId, filters);
      if (searchParams?.get('view') !== 'map') return baseUrl;

      const [pathname, queryString] = baseUrl.split('?');
      const params = new URLSearchParams(queryString);
      params.set('view', 'map');
      return `${pathname}?${params.toString()}`;
    },
    [filters, searchParams],
  );

  const pushUrl = useCallback(
    (url: string) => {
      startTransition(() => {
        router.push(url);
      });
    },
    [router],
  );

  // Persists the executed search (HP-8422); fixed slug because buildSearchUrl always targets apartments-for-rent.
  const persistRecentSearch = useCallback(
    (resourceId: string, filters: FilterState) => {
      if (!currentUser?.loggedIn) return;

      const knownArea = [currentArea, nearMeArea].find(
        (candidate) => candidate?.resourceId === resourceId && getAreaBounds(candidate),
      );

      void (knownArea ? Promise.resolve(knownArea) : areaApi.byResourceId(resourceId))
        .then((area) => {
          const bounds = getAreaBounds(area);
          if (!area?.id || !bounds) return;

          // `areas` is dropped for university areas (HPWEB-5750: unsearchable ids).
          const isUniversity = area.type === 'university';
          updateRecentSearch(
            buildRecentSearchBody({
              filters,
              searchSlug: 'apartments-for-rent',
              geo: {
                areaId: isUniversity ? null : area.id,
                lat: isUniversity ? undefined : area.coordinates?.lat,
                lon: isUniversity ? undefined : area.coordinates?.lon,
                bounds,
              },
            }),
          );
        })
        .catch(() => {
          // Fire-and-forget: a failed area lookup must never break navigation.
        });
    },
    [currentUser, currentArea, nearMeArea, updateRecentSearch],
  );

  // Single dispatch point for navigate-mode area searches so Enter-search,
  // button-submit, and click-select can't drift apart.
  const pushSearchUrl = useCallback(
    (resourceId: string) => {
      pushUrl(buildSearchUrl(resourceId, draftFilters));
      persistRecentSearch(resourceId, draftFilters);
    },
    [pushUrl, draftFilters, persistRecentSearch],
  );

  const setArea = useCallback(
    (result: AutocompleteResult) => {
      markUserInteraction();
      // Property autocomplete hits — navigate straight to the HDP regardless
      // of mode. The autocomplete API only returns a bare lotIdEncoded, not a
      // route, so the listing detail has to be fetched to get the canonical
      // /pad URL before we can push anywhere.
      if (result.lotIdEncoded) {
        const lotIdEncoded = result.lotIdEncoded;
        const suggestionResourceId = result.resourceId;
        latestListingRequestRef.current = lotIdEncoded;

        void resolveListingAutocompleteHref(lotIdEncoded).then((resolution) => {
          if (latestListingRequestRef.current !== lotIdEncoded) return undefined;

          if (resolution.type === 'href') {
            prepareMapForListingNavigation(lotIdEncoded);
            pushUrl(resolution.href);
            return undefined;
          }

          const resourceId = resolution.type === 'resourceId' ? resolution.resourceId : suggestionResourceId;
          if (!resourceId) return undefined;
          if (mode === 'live') {
            pushUrl(buildLiveAreaUrl(resourceId));
            persistRecentSearch(resourceId, filters);
          } else {
            pushSearchUrl(resourceId);
          }
          return undefined;
        });
        return;
      }
      if (mode === 'live') {
        if (result.resourceId) {
          if (result.resourceId !== currentArea?.resourceId) {
            prepareMapForAreaNavigation(result.resourceId);
          }
          pushUrl(buildLiveAreaUrl(result.resourceId));
          persistRecentSearch(result.resourceId, filters);
        }
        return;
      }
      setDraftArea(result);
    },
    [
      buildLiveAreaUrl,
      currentArea?.resourceId,
      filters,
      markUserInteraction,
      mode,
      persistRecentSearch,
      prepareMapForAreaNavigation,
      prepareMapForListingNavigation,
      pushUrl,
      pushSearchUrl,
    ],
  );

  const targetResourceId = draftArea?.resourceId ?? currentArea?.resourceId ?? nearMeArea?.resourceId ?? null;

  const submit = useCallback(() => {
    markUserInteraction();
    if (mode !== 'navigate' || !targetResourceId) return;
    pushSearchUrl(targetResourceId);
  }, [markUserInteraction, mode, targetResourceId, pushSearchUrl]);

  // Immediate search for a suggestion chosen via Enter in the location
  // combobox. Navigates with the result in hand instead of reading
  // `draftArea`, which hasn't flushed when selection and search share one
  // keystroke. Listings are excluded (`setArea` already navigates those in
  // every mode), as is live mode (selection itself navigates there — so on
  // the SRP an Enter-chosen suggestion still searches, via `setArea`).
  const searchArea = useCallback(
    (result: AutocompleteResult) => {
      markUserInteraction();
      if (mode !== 'navigate' || result.lotIdEncoded || !result.resourceId) return;
      pushSearchUrl(result.resourceId);
    },
    [markUserInteraction, mode, pushSearchUrl],
  );

  // Count modal-reachable filters only — Price, Beds, Baths sit on the
  // inline strip with their own active-state styling, so they shouldn't
  // double-count toward the All-filters badge.
  const activeFilterCount = useMemo(() => getMoreFiltersCount(filters), [filters]);

  const area = useMemo<AreaResponse | AutocompleteResult | null>(
    () => draftArea ?? currentArea ?? nearMeArea ?? null,
    [draftArea, currentArea, nearMeArea],
  );

  return {
    filters,
    setFilters,
    area,
    setArea,
    submit,
    searchArea,
    isPending,
    activeFilterCount,
    markUserInteraction,
  };
}
